import { useEffect, useState, useCallback } from 'react';
import { GymsService } from '../../../services/gymsService';
import GymFormModal from './GymFormModal';
import theme from '../adminTheme';
import AdminSpinner from '../components/AdminSpinner';
import ConfirmModal from '../components/ConfirmModal';
import AdminPagination from '../components/AdminPagination';

const textMuted = { color: '#6b7280' };
const badgeStyle = (estado) => {
  if (estado === 'activo') return theme.badgeActivo;
  if (estado === 'inactivo') return theme.badgeInactivo;
  return theme.badgeSuspendido;
};
const GymsView = () => {
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [gymEdit, setGymEdit] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Paginación y búsqueda
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const cargarGyms = useCallback(async () => {
    setLoading(true);
    try {
      const data = await GymsService.listar(page, 10, search);
      setGyms(data.items || []);
      setPagination(data.pagination || {
        page: 1,
        limit: 10,
        total: 0,
        total_pages: 1,
        has_next: false,
        has_prev: false
      });
    } catch (e) {
      console.error(e);
      setGyms([]);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    cargarGyms();
  }, [cargarGyms]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleNextPage = () => {
    if (pagination.has_next) {
      setPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (pagination.has_prev) {
      setPage(prev => prev - 1);
    }
  };

  const handleConfirmEliminar = async () => {
    if (!confirmDeleteId) return;
    setDeleting(true);
    try {
      await GymsService.eliminar(confirmDeleteId);
      setConfirmDeleteId(null);
      cargarGyms();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={theme.container}>
      <div style={theme.pageHeader}>
        <h2 style={theme.title}>Gyms afiliados a ACHIPO</h2>
        <form onSubmit={handleSearch} style={theme.searchContainer}>
          <input
            type="text"
            placeholder="Buscar por nombre o ciudad..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={theme.searchInput}
            aria-label="Buscar gyms"
          />
          <button type="submit" style={theme.btnGhost}>Buscar</button>
          {search && (
            <button type="button" style={theme.btnGhost} onClick={handleClearSearch}>
              Limpiar
            </button>
          )}
        </form>
        <button style={theme.btnPrimary} onClick={() => { setGymEdit(null); setShowModal(true); }} aria-label="Crear gym">
          Crear Gym
        </button>
      </div>

      {loading && <AdminSpinner label="Cargando gyms…" />}

      {!loading && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={theme.table}>
              <thead>
                <tr>
                  <th style={theme.th}>Nombre</th>
                  <th style={theme.th}>Club</th>
                  <th style={theme.th}>Estado</th>
                  <th style={{ ...theme.th, textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {gyms.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ ...theme.td, textAlign: 'center', color: '#666' }}>
                      {search ? `No se encontraron gyms para "${search}"` : 'No hay gyms registrados'}
                    </td>
                  </tr>
                ) : (
                  gyms.map((gym) => (
                    <tr key={gym.id}>
                      <td style={theme.td}>{gym.nombre}</td>
                      <td style={theme.td}>
                        {gym.club ? gym.club.nombre : <span style={textMuted}>Sin club</span>}
                      </td>
                      <td style={theme.td}>
                        <span style={{ ...theme.badge, ...badgeStyle(gym.estado) }}>{gym.estado}</span>
                      </td>
                      <td style={{ ...theme.td, textAlign: 'right' }}>
                        <div style={theme.btnRow}>
                          <button style={theme.btnGhost} onClick={() => { setGymEdit(gym); setShowModal(true); }} aria-label={`Editar ${gym.nombre}`}>Editar</button>
                          <button style={theme.btnDanger} onClick={() => setConfirmDeleteId(gym.id)} aria-label={`Eliminar ${gym.nombre}`}>Eliminar</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {gyms.length > 0 && (
            <AdminPagination
              page={pagination.page}
              totalPages={pagination.total_pages}
              total={pagination.total}
              pageSize={pagination.limit}
              hasPrev={pagination.has_prev}
              hasNext={pagination.has_next}
              onPrev={handlePrevPage}
              onNext={handleNextPage}
              label="gyms"
            />
          )}
        </>
      )}

      <ConfirmModal
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleConfirmEliminar}
        title="Eliminar gym"
        message="¿Eliminar este gym? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        loading={deleting}
      />

      {showModal && (
        <GymFormModal
          gym={gymEdit}
          onClose={() => setShowModal(false)}
          onSuccess={cargarGyms}
        />
      )}
    </div>
  );
};

export default GymsView;
